import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAction, useUpdate } from '../../engine/api/hooks';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Switch } from '../../engine/design/primitives/Input';
import { Badge, Banner, StatusBadge } from '../../engine/design/primitives/Misc';
import { formatDateTime } from '../../lib/format';
import type { ScreenContext } from '../../engine/screens/types';

// Mailbox detail → "Setup" tab: connection status (chip, last polled, error banner), the
// test / poll-now / connect-account actions, credential rotation (write-only fields) and the
// queue mapping. Everything goes through the engine hooks; nothing here sees a secret.

interface Check {
  name: string;
  ok: boolean;
  detail: string;
}

export function MailboxSetup({ id, row, refresh }: ScreenContext) {
  const [sp] = useSearchParams();
  const r = (row ?? {}) as Record<string, unknown>;
  const kind = String(r['kind'] ?? 'imap');
  const oauth = kind === 'graph' || kind === 'gmail';
  const [checks, setChecks] = useState<Check[] | null>(null);
  const [secrets, setSecrets] = useState({ password: '', client_secret: '' });
  const [queue, setQueue] = useState(String(r['queue_id'] ?? ''));

  const test = useAction<{ ok: boolean; checks: Check[] }>('mailboxes', 'test', { toast: { success: (res) => ((res as { ok: boolean }).ok ? 'Connection OK' : 'Some checks failed') }, onSuccess: (res) => { setChecks(res.checks); refresh(); } });
  const poll = useAction<{ processed: number; fetched: number; duplicates: number; ignored: number; error: string | null }>('mailboxes', 'poll', {
    toast: { success: (res) => { const s = res as { fetched: number; processed: number; error: string | null }; return s.error ? `Poll failed: ${s.error}` : `Fetched ${s.fetched}, processed ${s.processed}`; } },
    onSuccess: () => refresh(),
    invalidate: ['email-messages'],
  });
  const connect = useAction<{ url: string }>('mailboxes', 'oauth-start', { toast: { success: false }, onSuccess: (res) => window.open(res.url, '_blank', 'noopener') });
  const update = useUpdate('mailboxes', { toast: { success: 'Saved' }, onSuccess: () => { setSecrets({ password: '', client_secret: '' }); refresh(); } });

  const oauthFlag = sp.get('oauth');
  const lastError = r['last_error'] as string | null | undefined;

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-6)' }}>
      {oauthFlag === 'ok' && <Banner tone="pos">Account connected. Run a test to confirm access.</Banner>}
      {oauthFlag === 'error' && <Banner tone="error">Account connection failed{sp.get('detail') ? `: ${sp.get('detail')}` : ''}.</Banner>}
      {lastError && <Banner tone="error">{lastError}</Banner>}

      <section className="hf-stack" aria-label="Status">
        <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
          <StatusBadge status={String(r['status'] ?? '')} tones={{ ok: 'pos', error: 'neg' }} />
          <Badge tone={r['inbound_enabled'] ? 'info' : 'muted'}>{r['inbound_enabled'] ? 'Inbound on' : 'Inbound off'}</Badge>
          <Badge tone={r['outbound_enabled'] ? 'info' : 'muted'}>{r['outbound_enabled'] ? 'Outbound on' : 'Outbound off'}</Badge>
          <Badge tone={r['has_credentials'] ? 'pos' : 'warn'}>{r['has_credentials'] ? 'Credentials stored' : 'No credentials'}</Badge>
          {oauth && <Badge tone={r['has_refresh_token'] ? 'pos' : 'warn'}>{r['has_refresh_token'] ? 'Account connected' : 'Account not connected'}</Badge>}
          <span className="hf-muted" style={{ fontSize: 'var(--t-13)' }}>Last polled {r['last_polled_at'] ? formatDateTime(String(r['last_polled_at'])) : 'never'}</span>
        </div>
        <div className="hf-row" style={{ gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <Button icon="plug-zap" busy={test.isPending} onClick={() => test.mutate({ id })}>
            Test connection
          </Button>
          {kind !== 'smtp' && (
            <Button icon="refresh-cw" busy={poll.isPending} onClick={() => poll.mutate({ id })}>
              Poll now
            </Button>
          )}
          {oauth && (
            <Button icon="external-link" variant="primary" busy={connect.isPending} onClick={() => connect.mutate({ id })}>
              Connect account
            </Button>
          )}
        </div>
        {checks && (
          <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Connection checks">
            {checks.map((c) => (
              <li key={c.name} className="hf-row">
                <Badge tone={c.ok ? 'pos' : 'neg'}>{c.name}</Badge>
                <span style={{ fontSize: 'var(--t-13)' }}>{c.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="hf-form-section" aria-label="Credentials">
        <div className="hf-caps">Rotate credentials</div>
        <form
          className="hf-form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            const body: Record<string, string> = {};
            if (secrets.password) body['password'] = secrets.password;
            if (secrets.client_secret) body['client_secret'] = secrets.client_secret;
            if (Object.keys(body).length) update.mutate({ id: id!, body });
          }}
        >
          {!oauth && (
            <Field label="New password" help="Write-only: encrypted at rest, never returned.">
              <Input type="password" value={secrets.password} onChange={(e) => setSecrets((s) => ({ ...s, password: e.target.value }))} autoComplete="new-password" />
            </Field>
          )}
          {oauth && (
            <Field label="New client secret" help="Write-only: encrypted at rest, never returned.">
              <Input type="password" value={secrets.client_secret} onChange={(e) => setSecrets((s) => ({ ...s, client_secret: e.target.value }))} autoComplete="new-password" />
            </Field>
          )}
          <div className="hf-form-actions span-2" style={{ borderTop: 0, marginTop: 0 }}>
            <Button type="submit" busy={update.isPending} disabled={!secrets.password && !secrets.client_secret}>
              Rotate
            </Button>
          </div>
        </form>
      </section>

      <section className="hf-form-section" aria-label="Routing">
        <div className="hf-caps">Routing</div>
        <form
          className="hf-form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ id: id!, body: { queue_id: queue || null } });
          }}
        >
          <Field label="Queue id" help="Inbound mail from this mailbox lands in this service-desk queue.">
            <Input value={queue} onChange={(e) => setQueue(e.target.value)} placeholder="none" />
          </Field>
          <Field label="Delete after import" inline help="Off: imported mail is marked read, never removed.">
            <Switch checked={Boolean(r['delete_after_import'])} onChange={(e) => update.mutate({ id: id!, body: { delete_after_import: e.target.checked } })} />
          </Field>
          <div className="hf-form-actions span-2" style={{ borderTop: 0, marginTop: 0 }}>
            <Button type="submit" busy={update.isPending}>
              Save routing
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
